/**
 * The console shell.
 *
 * Plain nav names (D18): DECK · LEDGER · CONDITIONS · FAULTS · CELLS · GATES,
 * then ADMIN for an operator principal. Brand lives in the voice, the frame
 * and the palette, not in bird vocabulary a second person cannot read. CASE,
 * ORDER, CELL and FAULT are deep-link destinations reached from a row, not
 * nav entries — the feed IS the navigation.
 *
 * There is ONE app with one renderer, and ONE `<nav class="thumb">`. Below
 * 880px it is the sticky bottom bar; at and above 880px the SAME element is
 * the 184px left rail — grid placement and flex-direction, no second
 * component, no `display: none`, no CSS-hidden duplicate anywhere in `web/`.
 * That duplicate is what froze the old dashboard.
 *
 * ADMIN is the only operator-exclusive surface. Every other surface is a
 * CUSTOMER surface showing THAT principal's cases, positions, latency and
 * money in full — the tenant comes from the session, never from the URL.
 */

import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Component, useState, type ReactNode } from "react";

import { Admin } from "./components/Admin";
import { CaseView } from "./components/CaseView";
import { Cells, CellView } from "./components/Cells";
import { Conditions } from "./components/Conditions";
import { Deck } from "./components/Deck";
import { Equity, OrderView } from "./components/Equity";
import { Faults } from "./components/Faults";
import { Gates } from "./components/Gates";
import { api } from "./lib/api";
import { navigate, useRoute, useWideViewport, type Route } from "./lib/route";
import { setTzMode, tzMode } from "./lib/time";

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      // Historical reads are ETagged server-side; a short client stale time
      // keeps a tab switch from re-issuing every query at once. LIVE reads
      // override this with staleTime: 0 / gcTime: 0 at their call site.
      staleTime: 15_000,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <Shell />
    </QueryClientProvider>
  );
}

const NAV: Array<{ label: string; route: Route }> = [
  { label: "DECK", route: { name: "deck" } },
  { label: "LEDGER", route: { name: "ledger" } },
  { label: "CONDITIONS", route: { name: "conditions" } },
  { label: "FAULTS", route: { name: "faults" } },
  { label: "CELLS", route: { name: "cells" } },
  { label: "GATES", route: { name: "gates" } },
];

function Shell() {
  // Every hook before every early return: an auth-gated shell is exactly
  // where a conditional hook slips in unnoticed.
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, staleTime: 0 });
  const [route] = useRoute();
  const wide = useWideViewport();
  // 880 px is the RAIL breakpoint (tokens.css); 1100 is the case-beside-deck
  // one. The nav needs the first, not the second.
  const rail = useWideViewport(880);

  if (me.isLoading) return <div className="stub">…</div>;
  if (me.data?.state !== "authed") {
    return <Login needsSetup={me.data?.state === "needs_setup"} onDone={() => void me.refetch()} />;
  }

  const admin = !!me.data.admin;
  const username = me.data.username ?? "—";
  const tenant = me.data.tenant_id ?? "—";
  const caseOpen = route.name === "case";
  // On desktop the DECK stays beside an open case: the feed IS the
  // navigation. On phone the case owns the screen and the deck is NOT
  // MOUNTED — not hidden, not mounted. A CSS-hidden branch still builds its
  // DOM, still runs its effects and still polls `/state` every five seconds;
  // that is precisely the pattern that froze the old dashboard.
  const showMain = !caseOpen || wide;

  return (
    <div className={`app${caseOpen ? " app--case-open" : ""}`}>
      {showMain && (
        <main className="app__main">
          <Boundary what="this surface" reset={route.name}>
            {caseOpen ? <Deck /> : <Surface route={route} admin={admin} />}
          </Boundary>
        </main>
      )}

      {caseOpen && route.name === "case" && (
        <aside className="app__aside">
          {/* Only rendered where it is reachable. There is no display:none
              branch in the stylesheet to hide it on desktop. */}
          {!wide && (
            <button className="linkish app__back" onClick={() => navigate({ name: "deck" })}>
              ← deck
            </button>
          )}
          <Boundary what="this case" reset={route.postId}>
            <CaseView postId={route.postId} band={route.band} />
          </Boundary>
        </aside>
      )}

      <nav className="thumb" aria-label="surfaces">
        <div className="thumb__brand">
          {/* The mark's intrinsic ratio is 512:249. It is never given a square
              box again — that is what letterboxed it to ~7px tall.

              This is the BRAND cut, amber wing and all — byte-identical to
              brand/logo/svg/regrine-mark-reversed.svg, the variant the
              guidelines prescribe on Ink/dark backgrounds. It was briefly
              swapped for the mono cut on the reasoning that amber has one
              referent (money at risk) and a pinned logo therefore put amber
              on all seven surfaces. That reasoning was wrong, and the
              distinction is worth stating because it will come up again:

              the amber rule governs STATE. Amber means something because it
              is rare AND VARIABLE — it arrives when a position opens and
              leaves when it closes, which is what makes a lit deck readable
              across a room. The mark is INVARIANT: same asset, same corner,
              every surface, every state. Something that never changes cannot
              be read as a signal that changed. Suppressing it bought no
              legibility and cost the product its only identity in the
              authenticated app. The mono cuts are for one-colour uses —
              favicon, single-ink print — not for chassis. */}
          <img className="thumb__mark" src="/console/regrine-mark.svg" alt="" width={49} height={24} />
          {/* Mounted, not hidden: below the rail breakpoint the wordmark is
              240 px of a bar that already overflows, and the mark alone is
              unambiguous next to it. */}
          {rail && <span className="thumb__word">REGRINE</span>}
        </div>

        <div className="thumb__links">
          {NAV.map((n) => (
            <button
              key={n.label}
              className="thumb__link"
              onClick={() => navigate(n.route)}
              aria-current={route.name === n.route.name ? "page" : undefined}
            >
              {n.label}
            </button>
          ))}
          {admin && (
            <button
              className="thumb__link thumb__link--admin"
              onClick={() => navigate({ name: "admin" })}
              aria-current={route.name === "admin" ? "page" : undefined}
            >
              ADMIN
            </button>
          )}
        </div>

        <div className="thumb__foot">
          <button
            className="who"
            title={`signed in as ${username} · ${tenant} — sign out`}
            onClick={async () => {
              try {
                await api.logout();
              } finally {
                void me.refetch();
              }
            }}
          >
            {username}
          </button>
          <TzToggle />
        </div>
      </nav>
    </div>
  );
}

/**
 * One render error must not cost the operator the whole console.
 *
 * This is not defensive decoration. `bands.race.smart_calls` is an OBJECT and
 * was consumed as an array; the resulting `.map is not a function` unmounted
 * React's entire tree, so clicking ANY feed row produced a blank page — no
 * message, no nav, nothing to go back to. A blank page is also the single
 * worst failure mode this product has, because it is indistinguishable from a
 * dead bot.
 *
 * The rule it enforces is the surface's own: say what is not known. It names
 * the part that failed and prints the message verbatim — it never substitutes
 * a friendly sentence for a fact — and the nav rail stays mounted beside it,
 * so every other surface is still one tap away. `reset` re-mounts on
 * navigation so a bad case does not poison the next one.
 */
class Boundary extends Component<{ what: string; reset?: string; children: ReactNode }, { err: Error | null }> {
  state: { err: Error | null } = { err: null };
  static getDerivedStateFromError(err: Error) {
    return { err };
  }
  componentDidUpdate(prev: { reset?: string }) {
    if (prev.reset !== this.props.reset && this.state.err) this.setState({ err: null });
  }
  render() {
    if (!this.state.err) return this.props.children;
    return (
      <div className="stub stub--error">
        <div>{this.props.what} failed to render.</div>
        <p className="note mono">{String(this.state.err?.message ?? this.state.err)}</p>
      </div>
    );
  }
}

function Surface({ route, admin }: { route: Route; admin: boolean }) {
  switch (route.name) {
    case "deck":
      return <Deck />;
    case "order":
      return <OrderView tradeId={route.tradeId} />;
    case "cell":
      return <CellView source={route.source} instrument={route.instrument} direction={route.direction} />;
    case "cells":
      return <Cells />;
    case "conditions":
      return <Conditions />;
    case "gates":
      return <Gates admin={admin} />;
    case "ledger":
      return <Equity />;
    case "faults":
    case "fault":
      return <Faults />;
    case "admin":
      return <Admin admin={admin} />;
    default:
      return <Deck />;
  }
}

/** Clock mode. `clock()` and `stamp()` read `tzMode()` on every call, so
 *  invalidating the cache re-renders every timestamp in the document. The old
 *  `location.reload()` threw away the whole react-query cache, re-ran
 *  /auth/me and flashed a loading panel to change a date format. */
function TzToggle() {
  const [mode, setMode] = useState(tzMode);
  const next = mode === "utc" ? "local" : "utc";
  const word = (m: string) => (m === "utc" ? "UTC" : "LOCAL");
  return (
    <button
      className="tz"
      aria-label={`displayed times: ${
        mode === "utc" ? "UTC" : "Europe/Warsaw local"
      }. Switch to ${next === "utc" ? "UTC" : "Europe/Warsaw local"}.`}
      title="displayed times are Europe/Warsaw local unless switched to UTC; journals stay UTC"
      onClick={() => {
        setTzMode(next);
        setMode(next);
        void qc.invalidateQueries();
      }}
    >
      <span className="tz__k">times</span>
      <span className="tz__v">{word(mode)}</span>
    </button>
  );
}

function Login({ needsSetup, onDone }: { needsSetup: boolean; onDone: () => void }) {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (needsSetup) {
    return (
      <div className="login">
        <div className="login__card">
          <div className="login__brand">
            <img className="login__mark" src="/console/regrine-mark.svg" alt="" width={82} height={40} />
            <span className="login__word">REGRINE</span>
          </div>
          <p className="login__lede">
            no principal exists yet. Bootstrap is a console call, not a public endpoint — see the README.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      className="login"
      onSubmit={async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setErr(null);
        try {
          await api.login(u, p);
          onDone();
        } catch {
          setErr("invalid credentials");
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="login__card">
        <div className="login__brand">
          <img className="login__mark" src="/console/regrine-mark.svg" alt="" width={82} height={40} />
          <span className="login__word">REGRINE</span>
        </div>
        <p className="login__lede">your account, your money.</p>

        <label className="field" htmlFor="login-u">
          <span className="field__label">username</span>
          <input
            className="field__input mono"
            id="login-u"
            name="username"
            value={u}
            onChange={(e) => setU(e.target.value)}
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            required
          />
        </label>

        <label className="field" htmlFor="login-p">
          <span className="field__label">password</span>
          <input
            className="field__input"
            id="login-p"
            name="password"
            type="password"
            value={p}
            onChange={(e) => setP(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        <button className="login__submit" type="submit" disabled={busy}>
          {busy ? "signing in…" : "sign in"}
        </button>

        {/* Always rendered so the card cannot jump, and announced. A failed
            sign-in is not a negative number: it stays in the neutral ramp. */}
        <div className="login__err" role="alert" aria-live="assertive">
          {err ?? ""}
        </div>
      </div>
    </form>
  );
}
